import { Box, Breakpoint, styled, SxProps, Theme } from '@mui/system';
import React from 'react';
import { Palette } from 'Palette';
import { RADIUS, SPACING } from 'DesignTokens';

const StyledSection = styled(Box, {
  shouldForwardProp: (prop) =>
    ![
      'tone',
      'bottomSpacing',
      'leftRightSpacing',
      'child',
      'noSidePaddingFrom',
      'additionalClassName',
      'noTopPadding',
      'noTopPaddingFrom',
      'className',
      'transparent',
      'withBorder',
      'sx',
      'onAdd',
      'onMove',
      'onRemove',
      'onModifyActionButtons',
      'triggerAdd',
      'showEditButtons',
    ].includes(prop as string),
})<{
  tone?: keyof typeof Palette;
  bottomSpacing?: number;
  leftRightSpacing?: number;
  child?: boolean;
  noSidePaddingFrom?: Breakpoint;
  noSidePaddingTo?: Breakpoint;
  additionalClassName?: string;
  noTopPadding?: boolean;
  noTopPaddingFrom?: Breakpoint;
  transparent?: boolean;
  className?: string;
  withBorder?: boolean;
  sx?: SxProps<Theme>;
  onAdd?: unknown;
  onMove?: unknown;
  onRemove?: unknown;
  triggerAdd?: unknown;
  showEditButtons?: unknown;
  onModifyActionButtons?: unknown;
}>(({
  theme,
  tone = 'default',
  bottomSpacing,
  child,
  noSidePaddingFrom,
  noSidePaddingTo,
  noTopPadding,
  noTopPaddingFrom,
  additionalClassName = '',
  transparent,
  className,
  withBorder = false,
}) => {
  const baseStyles: any = {
    backgroundColor: transparent ? undefined : Palette[tone].background,
    className: `react-StyledSection ${className || ''}`.trim(),
    color: Palette[tone].text,
    paddingLeft: SPACING.sectionPaddingX,
    paddingRight: SPACING.sectionPaddingX,
    paddingTop: noTopPadding ? undefined : SPACING.sectionPaddingY,
    paddingBottom:
      bottomSpacing !== null && bottomSpacing !== undefined
        ? theme.spacing(bottomSpacing)
        : SPACING.sectionPaddingY,
    borderRadius: child ? RADIUS.sectionChild : RADIUS.section,
    border: withBorder ? '1px solid ' + Palette[tone].border : undefined,
    [theme.breakpoints.down('xs')]: {
      paddingLeft: '1rem',
      paddingRight: '1rem',
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
      minWidth: 0,
    },
    '.styled-section': {
      '& >.MuiBox-root': {
        paddingLeft: 0,
      },
    },
    ...(additionalClassName && { [`&.${additionalClassName}`]: {} }),
  };

  if (noSidePaddingFrom || noSidePaddingTo) {
    if (noSidePaddingFrom) {
      baseStyles[theme.breakpoints.up(noSidePaddingFrom)] = {
        paddingLeft: 0,
        paddingRight: 0,
      };
    } else {
      baseStyles[theme.breakpoints.down(noSidePaddingTo!)] = {
        paddingLeft: 0,
        paddingRight: 0,
      };
    }
  }

  if (noTopPaddingFrom && !noTopPadding) {
    baseStyles[theme.breakpoints.down(noTopPaddingFrom)] = {
      paddingTop: 0,
    };
  }

  return baseStyles;
});

// Wrapper Component — uses forwardRef so callers can attach refs (e.g. EOISection's rootRef)
const StyledSectionWrapper = React.forwardRef<
  HTMLElement,
  Omit<React.ComponentProps<typeof StyledSection>, 'className'>
>((props, ref) => {
  const { additionalClassName, ...rest } = props;
  return (
    <StyledSection
      {...rest}
      ref={ref}
      className={`styled-section ${additionalClassName || ''}`.trim()}
    />
  );
});

export default StyledSectionWrapper;
